import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import mapboxgl from 'mapbox-gl';
import 'mapbox-gl/dist/mapbox-gl.css';
import { fetchSkiAreas } from '../services/skiAreasService';
import { fetchSkiAreaMap } from '../services/mapManagementService';
import { useError } from '../contexts/ErrorContext';
import { MAPBOX_STYLE } from '../constants/mapbox';
import ElevationProfile from '../components/ElevationProfile';
import { baseElevation, meanHeight, topElevation } from '../components/skiAreaHeights';
import './MapViewerPage.css';

const MAPBOX_TOKEN = import.meta.env.VITE_MAPBOX_TOKEN;
const EMPTY = { type: 'FeatureCollection', features: [] };

const SOURCE_GPKG = 'gpkg';
const SOURCE_MAPBOX = 'mapbox';

const VIEW_2D = '2d';
const VIEW_3D = '3d';
const PITCH_3D = 60;
const BEARING_3D = -20;
const EXAGGERATIONS = [1, 1.5, 2, 3];

const BACKGROUND = '#0b1220';

// GPKG mode draws only what the file holds, so the base style is empty: a
// background, plus glyphs for contour and lift labels.
const GPKG_STYLE = {
  version: 8,
  glyphs: 'mapbox://fonts/mapbox/{fontstack}/{range}.pbf',
  sources: {},
  layers: [{ id: 'background', type: 'background', paint: { 'background-color': BACKGROUND } }],
};

const RUN_COLORS = [
  ['green', '#22c55e', 'Green'],
  ['blue', '#3b82f6', 'Blue'],
  ['red', '#ef4444', 'Red'],
  ['black', '#e2e8f0', 'Black'],
  ['double_black', '#a855f7', 'Double black'],
  ['grey', '#eab308', 'Terrain park'],
  ['orange', '#f97316', 'Freeride'],
  ['yellow', '#facc15', 'Extreme'],
];
const RUN_COLOR_FALLBACK = '#94a3b8';
const LIFT_COLOR = '#fb923c';
const POI_COLOR = '#fbbf24';
const BOUNDARY_COLOR = '#38bdf8';
const CONTOUR_MINOR = '#334155';
const CONTOUR_MAJOR = '#64748b';
const CONTOUR_LABEL = '#94a3b8';
const HIGHLIGHT = '#f8fafc';
const SELECTED = '#22d3ee';

const RUN_COLOR_EXPR = [
  'match', ['get', 'colorName'],
  ...RUN_COLORS.flatMap(([value, hex]) => [value, hex]),
  RUN_COLOR_FALLBACK,
];

const runColor = (colorName) =>
  (RUN_COLORS.find(([v]) => v === colorName) || [])[1] || RUN_COLOR_FALLBACK;
const runLabel = (colorName) =>
  (RUN_COLORS.find(([v]) => v === colorName) || [])[2] || colorName || 'Run';

const NO_MATCH = ['==', ['get', 'id'], '__none__'];

const formatM = (v) => (v == null ? '—' : `${Math.round(v).toLocaleString()} m`);

// layer id -> which toggle controls it
const GROUPS = {
  'msv-contour-minor': 'contours',
  'msv-contour-major': 'contours',
  'msv-contour-label': 'contours',
  'msv-boundary-fill': 'boundary',
  'msv-boundary-line': 'boundary',
  'msv-runs': 'runs',
  'msv-runs-hit': 'runs',
  'msv-run-selected': 'runs',
  'msv-lift-selected': 'lifts',
  'msv-lifts': 'lifts',
  'msv-lifts-hit': 'lifts',
  'msv-lift-label': 'lifts',
  'msv-pois': 'runs',
};

const HOVER_LAYERS = { 'msv-runs-hit': 'run', 'msv-lifts-hit': 'lift' };

// In 3D, deck.gl draws contours, runs and lifts; of the Mapbox layers only the
// boundary stays, flat on the base plane.
const KEPT_IN_3D = ['msv-boundary-fill', 'msv-boundary-line'];

const describe = (kind, p) => {
  const what = kind === 'run' ? runLabel(p.colorName)
    : String(p.liftType || 'lift').replace(/_/g, ' ');
  return `<strong>${p.name || 'Unnamed'}</strong>`
    + `<span class="msv-popup-kind">${what}</span>`
    + `<span class="msv-popup-stats">Top ${formatM(p.topElevationM)}`
    + ` · ${kind === 'run' ? 'Drop' : 'Rise'} ${formatM(p.verticalRiseM)}`
    + ` · ${formatM(p.lengthM)} long</span>`;
};


const asCollection = (geometry) => (geometry
  ? { type: 'FeatureCollection', features: [{ type: 'Feature', geometry, properties: {} }] }
  : EMPTY);


function boundsOf(geometry) {
  if (!geometry) return null;
  const rings = geometry.type === 'Polygon' ? geometry.coordinates
    : geometry.type === 'MultiPolygon' ? geometry.coordinates.flat() : [];
  const coords = rings.flat();
  if (coords.length === 0) return null;
  return coords.reduce(
    (acc, c) => acc.extend([c[0], c[1]]),
    new mapboxgl.LngLatBounds([coords[0][0], coords[0][1]], [coords[0][0], coords[0][1]]),
  );
}


const METRES_PER_DEGREE = 111320;

/** A copy of `center` moved `metres` along `bearing` (degrees clockwise from north). */
function shiftAlong(center, metres, bearing) {
  const c = mapboxgl.LngLat.convert(center);
  const rad = (bearing * Math.PI) / 180;
  const lat = c.lat + (metres * Math.cos(rad)) / METRES_PER_DEGREE;
  const lng = c.lng + (metres * Math.sin(rad)) / (METRES_PER_DEGREE * Math.cos((c.lat * Math.PI) / 180));
  return new mapboxgl.LngLat(lng, lat);
}

const lookAhead = (heightM, pitch) => heightM * Math.tan((pitch * Math.PI) / 180);

/** How far above the base plane the middle of the resort is drawn in 3D. */
const resortMidHeight = (data, exaggeration) => {
  const top = topElevation(data);
  return top == null ? 0 : ((top - baseElevation(data)) / 2) * exaggeration;
};

const EARTH_CIRCUMFERENCE_M = 40075016.686;

/**
 * Frame points drawn at a height above the base plane, with the given (or
 * current) pitch and bearing. Each point is `[lng, lat, heightM]`; flat content
 * passes 0.
 *
 * Mapbox fits bounds on the ground. A raised point is seen where the line of
 * sight through it meets the ground, height × tan(pitch) further along the
 * bearing, so each point is moved forward by its own amount before fitting. A
 * raised point is also nearer the camera by height / cos(pitch) and would look
 * larger than the fit, so the zoom steps out until the camera is as far from the
 * highest point as the fit put it from the ground. Mapbox's camera sits 1.5 ×
 * the canvas height (in pixels) from its target at the default 36.87° field of
 * view.
 */
function frameRaised(map, points, options) {
  if (points.length === 0) return;
  const { pitch = map.getPitch(), bearing = map.getBearing(), ...rest } = options;
  const seen = points.map(([lng, lat, h]) => shiftAlong([lng, lat], lookAhead(h, pitch), bearing));
  const bounds = seen.reduce((acc, c) => acc.extend(c), new mapboxgl.LngLatBounds(seen[0], seen[0]));
  const camera = map.cameraForBounds(bounds, { ...rest, pitch, bearing });
  if (!camera) return;
  const center = mapboxgl.LngLat.convert(camera.center);
  let { zoom } = camera;
  const highest = Math.max(...points.map(p => p[2]));
  if (highest > 0) {
    const metresPerPixel = (EARTH_CIRCUMFERENCE_M * Math.cos((center.lat * Math.PI) / 180))
      / (512 * 2 ** zoom);
    const fitDistance = 1.5 * map.getContainer().clientHeight * metresPerPixel;
    const nearer = highest / Math.cos((pitch * Math.PI) / 180);
    zoom -= Math.log2((fitDistance + nearer) / fitDistance);
  }
  map.easeTo({ ...camera, center, zoom, pitch, bearing, duration: options.duration ?? 0 });
}

function overlaySources(data) {
  return {
    'msv-contours': data.contours.contours || EMPTY,
    'msv-boundary': asCollection(data.skiArea.boundary),
    'msv-runs': data.runs,
    'msv-lifts': data.lifts,
    'msv-pois': data.pois,
    'msv-cursor': EMPTY,
  };
}


const OVERLAY_LAYERS = [
  { id: 'msv-contour-minor', type: 'line', source: 'msv-contours',
    filter: ['!', ['get', 'major']],
    paint: { 'line-color': CONTOUR_MINOR, 'line-width': 0.7 } },
  { id: 'msv-contour-major', type: 'line', source: 'msv-contours',
    filter: ['get', 'major'],
    paint: { 'line-color': CONTOUR_MAJOR, 'line-width': 1.3 } },
  { id: 'msv-contour-label', type: 'symbol', source: 'msv-contours',
    filter: ['get', 'major'], minzoom: 13,
    layout: {
      'symbol-placement': 'line',
      'text-field': ['to-string', ['get', 'elevationM']],
      'text-font': ['Open Sans Regular', 'Arial Unicode MS Regular'],
      'text-size': 10,
      'symbol-spacing': 320,
    },
    paint: {
      'text-color': CONTOUR_LABEL, 'text-halo-color': BACKGROUND, 'text-halo-width': 1.5,
    } },

  { id: 'msv-boundary-fill', type: 'fill', source: 'msv-boundary',
    paint: { 'fill-color': BOUNDARY_COLOR, 'fill-opacity': 0.04 } },
  { id: 'msv-boundary-line', type: 'line', source: 'msv-boundary',
    paint: { 'line-color': BOUNDARY_COLOR, 'line-width': 1.5, 'line-dasharray': [3, 2] } },

  // Halo beneath the lines, so the selected feature keeps its own colour.
  { id: 'msv-run-selected', type: 'line', source: 'msv-runs', filter: NO_MATCH,
    paint: { 'line-color': SELECTED, 'line-width': 9, 'line-opacity': 0.85 },
    layout: { 'line-cap': 'round', 'line-join': 'round' } },
  { id: 'msv-lift-selected', type: 'line', source: 'msv-lifts', filter: NO_MATCH,
    paint: { 'line-color': SELECTED, 'line-width': 8, 'line-opacity': 0.85 } },

  { id: 'msv-lifts', type: 'line', source: 'msv-lifts',
    paint: { 'line-color': LIFT_COLOR, 'line-width': 2 } },
  { id: 'msv-runs', type: 'line', source: 'msv-runs',
    paint: { 'line-color': RUN_COLOR_EXPR, 'line-width': 2.5 },
    layout: { 'line-cap': 'round', 'line-join': 'round' } },

  { id: 'msv-lift-label', type: 'symbol', source: 'msv-lifts', minzoom: 13,
    layout: {
      'symbol-placement': 'line',
      'text-field': ['coalesce', ['get', 'name'], ''],
      'text-font': ['Open Sans Regular', 'Arial Unicode MS Regular'],
      'text-size': 11,
    },
    paint: { 'text-color': '#fed7aa', 'text-halo-color': BACKGROUND, 'text-halo-width': 1.5 } },

  { id: 'msv-pois', type: 'circle', source: 'msv-pois',
    paint: {
      'circle-radius': 5, 'circle-color': POI_COLOR,
      'circle-stroke-color': BACKGROUND, 'circle-stroke-width': 2,
    } },

  // Wide transparent lines so a 2 px run is still easy to hover.
  { id: 'msv-lifts-hit', type: 'line', source: 'msv-lifts',
    paint: { 'line-color': '#000', 'line-opacity': 0, 'line-width': 14 } },
  { id: 'msv-runs-hit', type: 'line', source: 'msv-runs',
    paint: { 'line-color': '#000', 'line-opacity': 0, 'line-width': 14 } },

  { id: 'msv-cursor', type: 'circle', source: 'msv-cursor',
    paint: {
      'circle-radius': 6, 'circle-color': HIGHLIGHT,
      'circle-stroke-color': BACKGROUND, 'circle-stroke-width': 2,
    } },
];


const MapViewerPage = () => {
  const navigate = useNavigate();
  const { addError } = useError();
  const [searchParams, setSearchParams] = useSearchParams();
  const skiAreaId = searchParams.get('skiArea');
  const source = searchParams.get('source') === SOURCE_MAPBOX ? SOURCE_MAPBOX : SOURCE_GPKG;
  const view = searchParams.get('view') === VIEW_3D ? VIEW_3D : VIEW_2D;
  const is3d = source === SOURCE_GPKG && view === VIEW_3D;

  const [skiAreas, setSkiAreas] = useState([]);
  const [query, setQuery] = useState('');
  const [featureQuery, setFeatureQuery] = useState('');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [selected, setSelected] = useState(null);   // { kind: 'run'|'lift', id }
  const [visible, setVisible] = useState({ contours: true, boundary: true, runs: true, lifts: true });
  const [exaggeration, setExaggeration] = useState(1.5);
  const [cursor, setCursor] = useState(null);   // [lon, lat, z] under the profile crosshair
  const [deck3d, setDeck3d] = useState(null);   // skiArea3dLayers module, loaded on first 3D view

  const containerRef = useRef(null);
  const mapRef = useRef(null);
  const popupRef = useRef(null);
  const styleReadyRef = useRef(false);
  const hoverBoundRef = useRef(false);
  const overlayRef = useRef(null);     // deck.gl MapboxOverlay for the 3D view
  const stateRef = useRef({});
  stateRef.current = { data, source, visible, selected, is3d, exaggeration };

  useEffect(() => {
    let cancelled = false;
    fetchSkiAreas()
      .then(areas => { if (!cancelled) setSkiAreas(areas || []); })
      .catch(err => { if (!cancelled) addError(err.message, err.stack, 'Ski Area Viewer'); });
    return () => { cancelled = true; };
  }, [addError]);

  useEffect(() => {
    setSelected(null);
    setFeatureQuery('');
    if (!skiAreaId) { setData(null); return; }
    let cancelled = false;
    setLoading(true);
    setError(null);
    fetchSkiAreaMap(skiAreaId)
      .then(d => { if (!cancelled) setData(d); })
      .catch(err => {
        if (cancelled) return;
        setData(null);
        setError(err.message);
        addError(err.message, err.stack, 'Ski Area Viewer');
      })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [skiAreaId, addError]);

  const matches = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return [];
    return skiAreas
      .filter(a => (a.name || '').toLowerCase().includes(q) || a.id === q)
      .slice(0, 25);
  }, [skiAreas, query]);

  const setParam = useCallback((key, value) => {
    setSearchParams(prev => {
      const next = new URLSearchParams(prev);
      if (value) next.set(key, value); else next.delete(key);
      return next;
    }, { replace: key === 'source' || key === 'view' });
  }, [setSearchParams]);

  const featureIndex = useMemo(() => {
    const index = { run: new Map(), lift: new Map() };
    if (!data) return index;
    data.runs.features.forEach(f => index.run.set(f.properties.id, f));
    data.lifts.features.forEach(f => index.lift.set(f.properties.id, f));
    return index;
  }, [data]);
  const indexRef = useRef(featureIndex);
  indexRef.current = featureIndex;

  // Runs and lifts in the loaded resort whose name contains the query, or
  // whose id starts with it. Names that start with the query come first.
  const featureMatches = useMemo(() => {
    const q = featureQuery.trim().toLowerCase();
    if (!q || !data) return [];
    const rows = [];
    for (const [kind, fc] of [['run', data.runs], ['lift', data.lifts]]) {
      for (const f of fc.features) {
        const name = (f.properties.name || '').toLowerCase();
        const id = String(f.properties.id || '').toLowerCase();
        const at = name.indexOf(q);
        if (at === -1 && !id.startsWith(q)) continue;
        rows.push({ kind, feature: f, rank: at === 0 ? 0 : at > 0 ? 1 : 2 });
      }
    }
    rows.sort((a, b) => a.rank - b.rank
      || (a.feature.properties.name || '').localeCompare(b.feature.properties.name || ''));
    return rows.slice(0, 20);
  }, [featureQuery, data]);

  // Select a run or lift and frame the map on it, keeping the current tilt so
  // a 3D view stays 3D. Its layer is switched on if it was hidden.
  const focusFeature = useCallback((kind, feature) => {
    setSelected({ kind, id: feature.properties.id });
    setFeatureQuery('');
    const group = kind === 'run' ? 'runs' : 'lifts';
    setVisible(v => (v[group] ? v : { ...v, [group]: true }));
    const map = mapRef.current;
    const coords = feature.geometry?.coordinates || [];
    if (!map || coords.length === 0) return;
    const { is3d: in3d, exaggeration: ex, data: d } = stateRef.current;
    const base = in3d && d ? baseElevation(d) : 0;
    // Unknown heights take the line's mean. The 3D view interpolates them
    // instead, but the mean is close enough to frame by.
    const fallback = in3d ? (meanHeight(coords) ?? base) : base;
    const points = coords.map(c => [
      c[0], c[1],
      in3d ? ((c.length > 2 && Number.isFinite(c[2]) ? c[2] : fallback) - base) * ex : 0,
    ]);
    frameRaised(map, points, { padding: 80, maxZoom: 16, duration: 800 });
  }, []);

  const applyVisibility = useCallback((map) => {
    const { visible: v, is3d: in3d } = stateRef.current;
    for (const [layerId, group] of Object.entries(GROUPS)) {
      if (map.getLayer(layerId)) {
        const show = v[group] && (!in3d || KEPT_IN_3D.includes(layerId));
        map.setLayoutProperty(layerId, 'visibility', show ? 'visible' : 'none');
      }
    }
    if (map.getLayer('msv-cursor')) {
      map.setLayoutProperty('msv-cursor', 'visibility', in3d ? 'none' : 'visible');
    }
  }, []);

  const applySelection = useCallback((map) => {
    const { selected: sel } = stateRef.current;
    if (map.getLayer('msv-run-selected')) {
      map.setFilter('msv-run-selected',
        sel?.kind === 'run' ? ['==', ['get', 'id'], sel.id] : NO_MATCH);
    }
    if (map.getLayer('msv-lift-selected')) {
      map.setFilter('msv-lift-selected',
        sel?.kind === 'lift' ? ['==', ['get', 'id'], sel.id] : NO_MATCH);
    }
  }, []);

  const bindHover = useCallback((map) => {
    if (hoverBoundRef.current) return;
    hoverBoundRef.current = true;
    popupRef.current = new mapboxgl.Popup({
      closeButton: false, closeOnClick: false, offset: 12, className: 'msv-popup',
    });

    // One listener over both layers, so where a run crosses a lift only the
    // topmost feature (first in e.features) is described or selected.
    const layerIds = Object.keys(HOVER_LAYERS);
    map.on('mousemove', layerIds, (e) => {
      const f = e.features && e.features[0];
      if (!f) return;
      map.getCanvas().style.cursor = 'pointer';
      popupRef.current.setLngLat(e.lngLat)
        .setHTML(describe(HOVER_LAYERS[f.layer.id], f.properties)).addTo(map);
    });
    map.on('mouseleave', layerIds, () => {
      map.getCanvas().style.cursor = '';
      popupRef.current.remove();
    });
    map.on('click', layerIds, (e) => {
      const f = e.features && e.features[0];
      if (f) setSelected({ kind: HOVER_LAYERS[f.layer.id], id: f.properties.id });
    });
  }, []);

  // Add or refresh the GPKG layers. Idempotent, so it serves both a style load
  // and a new resort arriving on an already-loaded style.
  const applyOverlay = useCallback((map) => {
    const { data: d, source: s } = stateRef.current;
    if (!styleReadyRef.current || s !== SOURCE_GPKG || !d) return;
    for (const [id, geojson] of Object.entries(overlaySources(d))) {
      if (!map.getSource(id)) map.addSource(id, { type: 'geojson', data: geojson });
      else map.getSource(id).setData(geojson);
    }
    for (const layer of OVERLAY_LAYERS) {
      if (!map.getLayer(layer.id)) map.addLayer(layer);
    }
    applyVisibility(map);
    applySelection(map);
    bindHover(map);
  }, [applyVisibility, applySelection, bindHover]);

  // One map for the page's lifetime; switching source swaps its style.
  useEffect(() => {
    if (!containerRef.current || mapRef.current) return;
    if (!MAPBOX_TOKEN) { setError('VITE_MAPBOX_TOKEN is not set.'); return; }
    mapboxgl.accessToken = MAPBOX_TOKEN;
    const map = new mapboxgl.Map({
      container: containerRef.current,
      style: stateRef.current.source === SOURCE_MAPBOX ? MAPBOX_STYLE : GPKG_STYLE,
      center: [-30, 40],
      zoom: 1.5,
    });
    map.addControl(new mapboxgl.NavigationControl(), 'top-right');
    map.addControl(new mapboxgl.ScaleControl({ unit: 'metric' }), 'bottom-left');
    map.on('style.load', () => { styleReadyRef.current = true; applyOverlay(map); });
    mapRef.current = map;
    return () => {
      if (popupRef.current) popupRef.current.remove();
      map.remove();
      mapRef.current = null;
      overlayRef.current = null;
      styleReadyRef.current = false;
      hoverBoundRef.current = false;
    };
  }, [applyOverlay]);

  const lastSourceRef = useRef(source);
  useEffect(() => {
    const map = mapRef.current;
    if (!map || lastSourceRef.current === source) return;
    lastSourceRef.current = source;
    styleReadyRef.current = false;
    if (popupRef.current) popupRef.current.remove();
    map.setStyle(source === SOURCE_MAPBOX ? MAPBOX_STYLE : GPKG_STYLE);
  }, [source]);

  useEffect(() => {
    const map = mapRef.current;
    if (!map || !data) return;
    const bounds = boundsOf(data.skiArea.boundary);
    const { is3d: in3d, exaggeration: ex } = stateRef.current;
    if (bounds) {
      // In 3D, aim at mid-height of the resort rather than its base.
      const raised = in3d ? resortMidHeight(data, ex) : 0;
      frameRaised(map, [
        [bounds.getWest(), bounds.getSouth(), raised],
        [bounds.getEast(), bounds.getNorth(), raised],
      ], { padding: 40, pitch: in3d ? PITCH_3D : 0, bearing: in3d ? BEARING_3D : 0 });
    }
    else if (data.skiArea.centre?.lat != null) {
      map.jumpTo({ center: [data.skiArea.centre.lng, data.skiArea.centre.lat], zoom: 13 });
    }
    applyOverlay(map);
  }, [data, applyOverlay]);

  useEffect(() => {
    const map = mapRef.current;
    if (map && styleReadyRef.current) applyVisibility(map);
  }, [visible, applyVisibility]);

  // Switching between 2D and 3D tilts the camera and swaps which renderer
  // draws the lines.
  const lastIs3dRef = useRef(is3d);
  useEffect(() => {
    const map = mapRef.current;
    if (!map) return;
    if (styleReadyRef.current) applyVisibility(map);
    if (popupRef.current) popupRef.current.remove();
    if (lastIs3dRef.current !== is3d) {
      lastIs3dRef.current = is3d;
      // Keep the middle of the resort where it was on screen: going to 3D
      // looks ahead by its drawn mid-height, going back to 2D undoes that.
      const { data: d, exaggeration: ex } = stateRef.current;
      const height = d ? resortMidHeight(d, ex) : 0;
      const center = is3d
        ? shiftAlong(map.getCenter(), lookAhead(height, PITCH_3D), BEARING_3D)
        : shiftAlong(map.getCenter(), -lookAhead(height, map.getPitch()), map.getBearing());
      map.easeTo({
        center, pitch: is3d ? PITCH_3D : 0, bearing: is3d ? BEARING_3D : 0, duration: 800,
      });
    }
  }, [is3d, applyVisibility]);

  useEffect(() => {
    if (!is3d || deck3d) return;
    let cancelled = false;
    import('../components/skiArea3dLayers')
      .then(mod => { if (!cancelled) setDeck3d(mod); })
      .catch(err => { if (!cancelled) addError(err.message, err.stack, 'Ski Area Viewer'); });
    return () => { cancelled = true; };
  }, [is3d, deck3d, addError]);

  const prepared3d = useMemo(
    () => (deck3d && data ? deck3d.prepare3d(data) : null),
    [deck3d, data],
  );

  useEffect(() => {
    const map = mapRef.current;
    if (!map || !deck3d) return;
    let overlay = overlayRef.current;
    if (!overlay) {
      // A separate canvas over the map, synced to its camera. Empty in 2D.
      overlay = new deck3d.MapboxOverlay({
        interleaved: false,
        layers: [],
        // Same reach as the 14 px hit lines in 2D, so thin paths are easy to hover.
        pickingRadius: 7,
        getTooltip: ({ object }) => (object && object.kind
          ? { html: describe(object.kind, object.properties), className: 'msv-deck-tooltip' }
          : null),
      });
      map.addControl(overlay);
      overlayRef.current = overlay;
    }
    overlay.setProps({
      layers: is3d && prepared3d
        ? deck3d.buildDeckLayers(prepared3d, {
          exaggeration,
          visible,
          selected,
          cursor,
          runColor,
          liftColor: LIFT_COLOR,
          colors: {
            contourMinor: CONTOUR_MINOR,
            contourMajor: CONTOUR_MAJOR,
            selected: SELECTED,
            highlight: HIGHLIGHT,
            background: BACKGROUND,
          },
          onSelect: setSelected,
        })
        : [],
    });
  }, [deck3d, is3d, prepared3d, exaggeration, visible, selected, cursor]);

  useEffect(() => {
    const map = mapRef.current;
    if (map && styleReadyRef.current) applySelection(map);
  }, [selected, applySelection]);

  const onProfileHover = useCallback((coord) => {
    setCursor(coord || null);
    const map = mapRef.current;
    const src = map && map.getSource('msv-cursor');
    if (!src) return;
    src.setData(coord
      ? { type: 'FeatureCollection', features: [{ type: 'Feature', properties: {},
        geometry: { type: 'Point', coordinates: [coord[0], coord[1]] } }] }
      : EMPTY);
  }, []);

  const selectedFeature = selected ? featureIndex[selected.kind].get(selected.id) : null;
  const toggle = (key) => setVisible(v => ({ ...v, [key]: !v[key] }));
  const contourStatus = data?.contours?.status;

  return (
    <div className="msv-page">
      <div className="msv-header">
        <button className="msv-back" onClick={() => navigate('/map-management')}>
          &larr; Map Management
        </button>
        <h2>Ski Area Viewer</h2>
      </div>

      <div className="msv-controls">
        <div className="msv-picker">
          <input
            type="search"
            placeholder={skiAreas.length ? `Search ${skiAreas.length.toLocaleString()} ski areas` : 'Loading ski areas…'}
            value={query}
            onChange={(e) => setQuery(e.target.value)}
          />
          {matches.length > 0 && (
            <ul className="msv-matches">
              {matches.map(a => (
                <li key={a.id}>
                  <button onClick={() => { setParam('skiArea', a.id); setQuery(''); }}>
                    <span>{a.name}</span>
                    <span className="msv-match-meta">{[a.region, a.country].filter(Boolean).join(', ')} · {a.id}</span>
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>

        <div className="msv-segmented" role="tablist" aria-label="Map source">
          <button role="tab" aria-selected={source === SOURCE_GPKG}
            className={source === SOURCE_GPKG ? 'active' : ''}
            onClick={() => setParam('source', SOURCE_GPKG)}>
            GPKG
          </button>
          <button role="tab" aria-selected={source === SOURCE_MAPBOX}
            className={source === SOURCE_MAPBOX ? 'active' : ''}
            onClick={() => setParam('source', SOURCE_MAPBOX)}>
            Mapbox style
          </button>
        </div>
      </div>

      {data && (
        <div className="msv-resort">
          <strong>{data.skiArea.name}</strong>
          <span>{[data.skiArea.region, data.skiArea.country].filter(Boolean).join(', ')}</span>
          <span className="msv-chip">{data.skiArea.id}</span>
          <span className="msv-chip">{data.runs.features.length} runs</span>
          <span className="msv-chip">{data.lifts.features.length} lifts</span>
          {source === SOURCE_GPKG && contourStatus === 'ok' && (
            <span className="msv-chip">
              {data.contours.contours.features.length.toLocaleString()} contours
              {data.contours.intervalM ? ` · ${data.contours.intervalM} m / ${data.contours.majorIntervalM} m` : ''}
            </span>
          )}
          {source === SOURCE_GPKG && contourStatus === 'missing' && (
            <span className="msv-chip warn">No contour block</span>
          )}
          {source === SOURCE_GPKG && contourStatus === 'unsupported' && (
            <span className="msv-chip warn">Version has no contours</span>
          )}
          <span className="msv-version">Version {data.versionId}</span>
        </div>
      )}

      {source === SOURCE_GPKG && (
        <div className="msv-toolbar">
          <div className="msv-segmented msv-segmented-small" role="tablist" aria-label="View">
            <button role="tab" aria-selected={!is3d} className={!is3d ? 'active' : ''}
              onClick={() => setParam('view', null)}>
              2D
            </button>
            <button role="tab" aria-selected={is3d} className={is3d ? 'active' : ''}
              onClick={() => setParam('view', VIEW_3D)}>
              3D
            </button>
          </div>
          {is3d && (
            <label>
              Vertical
              <select value={exaggeration} onChange={(e) => setExaggeration(Number(e.target.value))}>
                {EXAGGERATIONS.map(x => <option key={x} value={x}>{x}×</option>)}
              </select>
            </label>
          )}
          <span className="msv-divider" />
          {[['contours', 'Contours'], ['boundary', 'Boundary'], ['runs', 'Runs'], ['lifts', 'Lifts']].map(([key, label]) => (
            <label key={key}>
              <input type="checkbox" checked={visible[key]} onChange={() => toggle(key)} />
              {label}
            </label>
          ))}
          {data && (
            <div className="msv-feature-search">
              <input
                type="search"
                placeholder="Find a run or lift"
                aria-label="Find a run or lift"
                value={featureQuery}
                onChange={(e) => setFeatureQuery(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && featureMatches[0]) {
                    focusFeature(featureMatches[0].kind, featureMatches[0].feature);
                  } else if (e.key === 'Escape') {
                    setFeatureQuery('');
                  }
                }}
              />
              {featureQuery.trim() && (
                <ul className="msv-matches">
                  {featureMatches.length === 0 && <li className="msv-no-match">No runs or lifts match</li>}
                  {featureMatches.map(({ kind, feature }) => {
                    const p = feature.properties;
                    return (
                      <li key={`${kind}:${p.id}`}>
                        <button onClick={() => focusFeature(kind, feature)}>
                          <span className="msv-match-name">
                            <span className="msv-line" style={{
                              background: kind === 'run' ? runColor(p.colorName) : LIFT_COLOR,
                            }} />
                            {p.name || 'Unnamed'}
                          </span>
                          <span className="msv-match-meta">
                            {kind === 'run' ? runLabel(p.colorName) : String(p.liftType || 'lift').replace(/_/g, ' ')}
                            {' · '}{p.id}
                          </span>
                        </button>
                      </li>
                    );
                  })}
                </ul>
              )}
            </div>
          )}
        </div>
      )}

      {error && <div className="msv-error">{error}</div>}

      <div className="msv-canvas-wrap">
        <div ref={containerRef} className="msv-canvas" />
        {!skiAreaId && <div className="msv-overlay">Search for a ski area</div>}
        {loading && <div className="msv-overlay">Loading map…</div>}
      </div>

      {source === SOURCE_GPKG && data && (
        <div className="msv-legend">
          <div className="msv-legend-group">
            <span className="msv-legend-title">Runs</span>
            {RUN_COLORS.map(([value, hex, label]) => (
              <span className="msv-legend-item" key={value}>
                <span className="msv-line" style={{ background: hex }} />{label}
              </span>
            ))}
          </div>
          <div className="msv-legend-group">
            <span className="msv-legend-title">Also</span>
            <span className="msv-legend-item">
              <span className="msv-line" style={{ background: LIFT_COLOR }} />Lift
            </span>
            <span className="msv-legend-item">
              <span className="msv-line msv-line-dashed" />Boundary
            </span>
            <span className="msv-legend-item">
              <span className="msv-line" style={{ background: CONTOUR_MAJOR }} />Major contour
            </span>
            <span className="msv-legend-item">
              <span className="msv-line msv-line-thin" style={{ background: CONTOUR_MINOR }} />Minor contour
            </span>
          </div>
        </div>
      )}

      {source === SOURCE_GPKG && selectedFeature && (
        <div className="msv-selection">
          <div className="msv-selection-head">
            <span className="msv-line" style={{
              background: selected.kind === 'run' ? runColor(selectedFeature.properties.colorName) : LIFT_COLOR,
            }} />
            <strong>{selectedFeature.properties.name || 'Unnamed'}</strong>
            <span className="msv-selection-kind">
              {selected.kind === 'run'
                ? runLabel(selectedFeature.properties.colorName)
                : String(selectedFeature.properties.liftType || 'lift').replace(/_/g, ' ')}
            </span>
            <span className="msv-chip">{selectedFeature.properties.id}</span>
            <button className="msv-close" onClick={() => setSelected(null)} aria-label="Clear selection">×</button>
          </div>
          <div className="msv-stats">
            <div><span>Top</span>{formatM(selectedFeature.properties.topElevationM)}</div>
            <div><span>{selected.kind === 'run' ? 'Vertical drop' : 'Vertical rise'}</span>{formatM(selectedFeature.properties.verticalRiseM)}</div>
            <div><span>Length</span>{formatM(selectedFeature.properties.lengthM)}</div>
            {data.terrain && (
              <div>
                <span>Heights</span>
                {data.terrain.datum} · {(data.terrain.sources || []).join(', ')}
              </div>
            )}
          </div>
          <ElevationProfile
            coordinates={selectedFeature.geometry?.coordinates || []}
            color={selected.kind === 'run' ? runColor(selectedFeature.properties.colorName) : LIFT_COLOR}
            onHover={onProfileHover}
          />
        </div>
      )}
    </div>
  );
};

export default MapViewerPage;
